import classNames from "classnames";
import { allUserRoles, ENTITY_TYPE } from "constants/phase";
import amplitude from "externals/amplitude";
import { useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import { selectSelectedEntityId, selectSelectedEntityRole, selectSelectedEntityType } from "store/entity/selectors";

import {
  selectAppAdditionalPermissionsAdvanceEditorBySelectedUserId,
  selectAppAdditionalPermissionsGraphicsTemplatesBySelectedUserId,
  selectAppAdditionalPermissionShowArchivalBySelectedUserId,
  selectAppAdditionalPermissionsRuleManagementBySelectedUserId,
} from "../../../../store/entityConfig/selectors";
import { selectSelectedUserIsSettingsActionAllowed } from "../../../../store/role/selectors";
import SVGIcon from "../../Images/SVGIcon";
import styles from "./style.module.css";

const NavigationBar = ({
  appAdditionalPermissionsRuleManagement,
  isSettingsActionAllowed,
  appAdditionalPermissionsAdvanceEditor,
  appAdditionalPermissionsShowArchival,
  entityRole,
  entityId,
  entityType,
  appAdditionalPermissionsGraphicsTemplates,
  isExpanded,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const settingsPath = useMemo(() => {
    if (entityRole !== allUserRoles.superAdmin) {
      return `/${
        entityType === ENTITY_TYPE.ORGANIZATION ? "organizations-profile" : "workspaces-profile"
      }/${entityId}/basicinfo`;
    }

    return "/configuration/category";
  }, [entityRole, entityId, entityType]);

  const navigationItems = useMemo(
    () => [
      ...(entityRole === allUserRoles.superAdmin
        ? [
            {
              key: "overview",
              label: "Overview",
              isActive: pathname.startsWith("/overview"),
            },
            {
              key: "organization-management",
              label: "Organizations",
              isActive: pathname.startsWith("/organization-management"),
            },
            {
              key: "user-management",
              label: "Users",
              isActive: pathname.startsWith("/user-management"),
            },
            {
              key: "ops-management",
              label: "Ops",
              isActive: pathname.startsWith("/ops-management"),
            },
          ]
        : [
            {
              key: "dashboard",
              label: "Home",
              isActive: pathname.startsWith("/dashboard"),
            },
            {
              key: "videos",
              label: "Videos",
              isActive: pathname.startsWith("/videos"),
            },
            {
              key: "my-highlights",
              label: "Highlights",
              isActive: pathname.startsWith("/my-highlights"),
            },
            {
              key: "publish-history",
              label: "Published",
              isActive: pathname.startsWith("/publish-history"),
            },
          ]),
      ...(appAdditionalPermissionsRuleManagement
        ? [
            {
              key: "rule-listing",
              label: "Rules",
              isActive: pathname.startsWith("/rule-listing"),
            },
          ]
        : []),
      ...(appAdditionalPermissionsShowArchival
        ? [
            {
              key: "archive",
              label: "Archive",
              isActive: pathname.startsWith("/archive"),
            },
          ]
        : []),
      ...(appAdditionalPermissionsAdvanceEditor
        ? [
            {
              key: "studio",
              label: "Studio",
              isActive: ["/studio", "/studio/"].includes(pathname), // as it should not be active in case such as studio/clips or studio/highlights
              applyFilter: false,
            },
          ]
        : []),
      ...(appAdditionalPermissionsGraphicsTemplates
        ? [
            {
              key: "graphics",
              label: "Graphics",
              isActive: pathname.startsWith("/graphics"),
            },
          ]
        : []),
      ...(isSettingsActionAllowed
        ? [
            {
              key: "configuration/category",
              label: "Settings",
              isActive: pathname.startsWith(`/${settingsPath.split("/")[1]}`),
            },
          ]
        : []),
    ],
    [
      entityRole,
      pathname,
      appAdditionalPermissionsRuleManagement,
      appAdditionalPermissionsShowArchival,
      appAdditionalPermissionsAdvanceEditor,
      appAdditionalPermissionsGraphicsTemplates,
      isSettingsActionAllowed,
      settingsPath,
    ],
  );

  const onClick = useCallback(
    ({ isActive, key }) =>
      () => {
        if (!isActive) {
          amplitude.logEvent("sidebar_tab_clicked ", {
            page: key,
          });

          if (key === "configuration/category") {
            navigate(settingsPath);

            return;
          }
          navigate(`/${key}`);
        }
      },
    [navigate, settingsPath],
  );

  return (
    <div className={styles.container} data-expanded={String(isExpanded)}>
      {navigationItems.map(({ key, label, isActive, applyFilter = true }) => {
        return (
          <div
            key={key}
            className={classNames(styles.itemContainer, {
              [styles.itemContainerActive]: isActive,
            })}
            onClick={onClick({ isActive, key })}
            data-tip={label}
          >
            <span className={styles.ico}>
              <SVGIcon
                src={`components/v2/Sidebar/NavigationBar/${key}.svg`}
                className={classNames(styles.icon, {
                  [styles.iconActive]: isActive && applyFilter,
                })}
              />
            </span>
            <div
              className={classNames(styles.label, {
                [styles.labelActive]: isActive,
              })}
              data-testid={`NavigationBar-${key}`}
            >
              {label}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  appAdditionalPermissionsRuleManagement: selectAppAdditionalPermissionsRuleManagementBySelectedUserId,
  isSettingsActionAllowed: selectSelectedUserIsSettingsActionAllowed,
  appAdditionalPermissionsAdvanceEditor: selectAppAdditionalPermissionsAdvanceEditorBySelectedUserId,
  appAdditionalPermissionsShowArchival: selectAppAdditionalPermissionShowArchivalBySelectedUserId,
  entityRole: selectSelectedEntityRole,
  entityId: selectSelectedEntityId,
  entityType: selectSelectedEntityType,
  appAdditionalPermissionsGraphicsTemplates: selectAppAdditionalPermissionsGraphicsTemplatesBySelectedUserId,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(NavigationBar);
